// Wave 3.99ae — Internal mail viewer (participant + Super Admin covert audit).
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Reply, ReplyAll, Trash2, Download, Flag, FileText, CheckCheck, Eye, Clock,
  ShieldAlert, X, EyeOff,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { formatDateTime } from '@/lib/format';
import { prettySize, fileKind, downloadDataUrl } from '@/lib/mail';

// Sanitise a stored mail body before rendering. Allows the formatting tags the
// Compose editor emits (bold / colour / lists / links) plus inline images
// (base64 data URIs). Everything else — scripts, event handlers — is stripped.
const renderBodyHtml = (body) => DOMPurify.sanitize(body || '', {
  ALLOWED_TAGS: [
    'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'span', 'div',
    'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5',
    'h6', 'pre', 'code', 'font', 'hr',
  ],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'style', 'target', 'rel', 'width', 'height', 'color', 'class'],
});
// New rich bodies contain markup; legacy plain-text bodies do not.
const looksLikeHtml = (s) => /<[a-z][\s\S]*>/i.test(s || '');

export const MessageViewerDialog = ({ open, messageId, admin = false, onClose, onReply, onReplyAll, onDeleted }) => {
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !messageId) { setMsg(null); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const url = admin ? `/mail/admin/${messageId}` : `/mail/${messageId}`;
        const { data } = await api.get(url);
        if (!alive) return;
        setMsg(data);
        // Recipient viewing marks the message read (drives sender's receipt).
        // Super Admin audit is COVERT — never marks read.
        if (!admin && data && !data.is_sender) {
          try { await api.post(`/mail/${messageId}/read`); } catch { /* noop */ }
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Message not available');
        onClose && onClose();
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, messageId, admin]);

  const recipients = msg?.recipients || [];
  const isSender = !!msg?.is_sender;
  const canReplyAll = recipients.length > 1;
  const showReceipts = admin || isSender;

  const doDelete = async (scope) => {
    try {
      await api.delete(`/mail/${messageId}`, { params: { scope } });
      toast.success(scope === 'all' ? 'Deleted for everyone' : 'Deleted');
      setConfirmOpen(false);
      onClose && onClose();
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
        <DialogContent className="max-w-2xl" data-testid="mail-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6">
              {msg?.priority && <Flag size={16} className="text-rose-600 shrink-0" data-testid="mail-viewer-priority-flag" />}
              <span className="truncate">{msg?.subject || (loading ? 'Loading…' : 'Message')}</span>
              {msg?.disappearing && <Badge className="ml-1 bg-rose-50 text-rose-600 border border-rose-200 text-[10px]" data-testid="mail-viewer-disappearing"><EyeOff size={11} className="mr-1" />Viewable once</Badge>}
              {admin && <Badge className="ml-1 bg-amber-100 text-amber-800 border border-amber-200 text-[10px]"><ShieldAlert size={11} className="mr-1" />Audit</Badge>}
            </DialogTitle>
          </DialogHeader>

          {loading || !msg ? (
            /* Wave 3.99af — Structured skeleton (replaces the bare spinner).
               Reserves the exact layout for From/To/Date, the body block and
               the read-receipts list so there is no jump when data arrives.
               Covert semantics are unchanged — this is purely presentational
               while the fetch in the effect above resolves. */
            <div className="space-y-3" data-testid="mail-viewer-loading">
              <div className="space-y-2">
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-56" />
                <Skeleton className="h-3 w-32" />
              </div>
              <div className="rounded-lg border border-[var(--divider)] bg-white px-3 py-3 space-y-2 min-h-[80px]">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-11/12" />
                <Skeleton className="h-3 w-4/5" />
              </div>
              {(admin) && (
                <div className="rounded-lg border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2 space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 space-y-1">
                <div><span className="font-medium text-slate-600">From:</span> {msg.sender?.name || msg.sender?.email}</div>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="font-medium text-slate-600">To:</span>
                  {recipients.map((r) => (
                    <Badge key={r.user_id} variant="secondary" className="border border-[var(--divider)] bg-[var(--paper-warm)] text-slate-600 text-[10px]">{r.name}</Badge>
                  ))}
                </div>
                <div><span className="font-medium text-slate-600">Date:</span> {formatDateTime(msg.created_at)}</div>
              </div>

              {looksLikeHtml(msg.body) ? (
                <div
                  className="rounded-lg border border-[var(--divider)] bg-white px-3 py-3 text-sm text-slate-800 min-h-[80px] mail-html-body"
                  data-testid="mail-viewer-body"
                  dangerouslySetInnerHTML={{ __html: renderBodyHtml(msg.body) }}
                />
              ) : (
                <div className="rounded-lg border border-[var(--divider)] bg-white px-3 py-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[80px]" data-testid="mail-viewer-body">
                  {msg.body || '(no message body)'}
                </div>
              )}

              {(msg.attachments || []).length > 0 && (
                <div className="space-y-1.5" data-testid="mail-viewer-attachments">
                  <div className="text-xs font-medium text-slate-500">Attachments ({msg.attachments.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {msg.attachments.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => downloadDataUrl(a.name, a.data_url)}
                        className="inline-flex items-center gap-2 rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-100 transition-colors"
                        data-testid={`mail-viewer-attachment-${a.id}`}
                        title={`Download ${a.name}`}
                      >
                        <FileText size={13} className="text-[var(--diamond-icon)]" />
                        <span className="max-w-[180px] truncate">{a.name}</span>
                        <span className="text-[10px] text-slate-400">{fileKind(a.mime, a.name)} · {prettySize(a.size)}</span>
                        <Download size={13} className="text-slate-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {showReceipts && (
                <div className="rounded-lg border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2" data-testid="mail-viewer-receipts">
                  <div className="text-xs font-medium text-slate-500 mb-1.5">Read receipts</div>
                  <div className="space-y-1.5">
                    {recipients.map((r) => (
                      <div key={r.user_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`mail-viewer-receipt-${r.user_id}`}>
                        <span className="text-slate-700 truncate">{r.name}</span>
                        <span className="flex items-center gap-3 text-slate-500 font-mono tabular-nums">
                          <span className="flex items-center gap-1"><CheckCheck size={12} className={r.delivered ? 'text-[var(--diamond-icon)]' : 'text-slate-300'} />{r.delivered_at ? formatDateTime(r.delivered_at) : '—'}</span>
                          <span className="flex items-center gap-1"><Eye size={12} className={r.read ? 'text-[var(--diamond-icon)]' : 'text-slate-300'} />{r.read_at ? formatDateTime(r.read_at) : '—'}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!admin && (
                <div className="flex items-center gap-2 pt-1">
                  <Button variant="outline" size="sm" className="h-9" onClick={() => onReply && onReply(msg)} data-testid="mail-viewer-reply">
                    <Reply size={14} className="mr-1.5" /> Reply
                  </Button>
                  {canReplyAll && (
                    <Button variant="outline" size="sm" className="h-9" onClick={() => onReplyAll && onReplyAll(msg)} data-testid="mail-viewer-reply-all">
                      <ReplyAll size={14} className="mr-1.5" /> Reply all
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-9 text-rose-600 hover:bg-rose-50 ml-auto" onClick={() => setConfirmOpen(true)} data-testid="mail-viewer-delete">
                    <Trash2 size={14} className="mr-1.5" /> Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — sender gets scope choice, recipient only self. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="mail-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              {isSender
                ? 'You can remove it just from your own mailbox, or delete it for everyone (it will disappear from all recipients’ inboxes too).'
                : 'This removes the message from your mailbox only. The priority marking is never removed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {isSender ? (
              <>
                <Button variant="outline" onClick={() => doDelete('self')} data-testid="mail-delete-self">Delete for me</Button>
                <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => doDelete('all')} data-testid="mail-delete-all">Delete for everyone</AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => doDelete('self')} data-testid="mail-delete-self">Delete</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MessageViewerDialog;
